import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useExport } from './header/exporters';
import { formatTime, formatWeekdayDayMonth } from '../utils/formatDate';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';

import '../styles/components/CalendarView.css';
import { loadCalendarEvents, saveCalendarEvents } from '../services/CalendarService';
import { CalendarEvent } from '../types/Planner';
import { loadTasks, refreshTasksFromApi, Task } from '../services/TasksService';
import { useStoredEvents } from '../hooks/useStoredEvents';
import { loadBookings } from '../services/VendorBookingService';
import { loadProjectVendors } from '../data/VendorLoader';
import type { ParsedVendor } from '../data/vendorData';
import type { VendorBooking } from '../types/Vendor';
import { loadAssignments, refreshAssignmentsFromApi } from '../services/LogisticsService';
import type { GuestAssignment } from '../types/ui/Logistics';
import { useNotifications } from './NotificationProvider';

// Same "confirmed vs pending" split used elsewhere for vendor bookings —
// booked/contract-signed/paid-in-full read as settled, everything earlier
// in the pipeline (including a signed-but-unpaid contract) still pending.
const CONFIRMED_STATUSES = new Set(['booked', 'contract-signed', 'paid-in-full']);

const HOUR_HEIGHT = 64;
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 22;
const VISIBLE_HOURS = DAY_END_HOUR - DAY_START_HOUR;

const CATEGORY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    ceremony:   { bg: '#fdf2ff', border: '#e879f9', text: '#9333ea' },
    reception:  { bg: '#eff6ff', border: '#60a5fa', text: '#2563eb' },
    cocktail:   { bg: '#f0fdf4', border: '#34d399', text: '#059669' },
    afterparty: { bg: '#fff7ed', border: '#fb923c', text: '#ea580c' },
    planning:   { bg: '#f0edff', border: '#818cf8', text: '#4f46e5' },
    vendor:     { bg: '#fefce8', border: '#facc15', text: '#ca8a04' },
    rehearsal:  { bg: '#f8fafc', border: '#94a3b8', text: '#64748b' },
    official:   { bg: '#fdf2ff', border: '#c084fc', text: '#7e22ce' },
    social:     { bg: '#f0fdf4', border: '#4ade80', text: '#16a34a' },
    academic:   { bg: '#eff6ff', border: '#38bdf8', text: '#0284c7' },
    task:       { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
};

const CATEGORY_OPTIONS = [
    { value: 'ceremony',   label: 'Ceremony' },
    { value: 'reception',  label: 'Reception' },
    { value: 'cocktail',   label: 'Cocktail Hour' },
    { value: 'afterparty', label: 'After Party' },
    { value: 'planning',   label: 'Planning' },
    { value: 'vendor',     label: 'Vendor Meeting' },
    { value: 'rehearsal',  label: 'Rehearsal' },
    { value: 'official',   label: 'Official' },
    { value: 'social',     label: 'Social' },
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n: number) => n.toString().padStart(2, '0');
const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
};

const addDays = (date: Date, n: number): Date => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
};

type ViewMode = 'week' | 'month';
type EventCategory = CalendarEvent['category'];

interface CalendarViewProps {
    onControlsReady?: (controls: {
        viewMode: ViewMode;
        label: string;
        prev: () => void;
        next: () => void;
        today: () => void;
        setViewMode: (m: ViewMode) => void;
    }) => void;
    onAddEvent?: (prefill: { date: string; startTime: string }) => void;
    onEditEvent?: (eventId: string) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ onControlsReady, onAddEvent, onEditEvent }) => {
    const navigate = useNavigate();
    const { showSuccess } = useNotifications();
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [viewMode, setViewMode] = useState<ViewMode>('week');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const storedEvents = useStoredEvents();
    const projectEvents = useMemo(() => storedEvents.filter(e => !e.archived), [storedEvents]);
    const [vendorBookings, setVendorBookings] = useState<Record<string, VendorBooking>>({});
    const [vendors, setVendors] = useState<ParsedVendor[]>([]);
    const [assignments, setAssignments] = useState<GuestAssignment[]>([]);

    const hasAutoJumped = useRef(false);

    useEffect(() => {
        // Render whatever is cached immediately, then refresh from the API —
        // on a direct page load the caches are cold and would stay empty
        setEvents(loadCalendarEvents());
        setTasks(loadTasks().filter(t => t.dueDateISO && !t.archived && t.status !== 'Done'));
        void refreshTasksFromApi().then(fresh => {
            const open = fresh.filter(t => t.dueDateISO && !t.archived && t.status !== 'Done');
            setTasks(open);
            // If the current week has nothing due, jump to the next upcoming
            // deadline so the planner opens onto something actionable
            if (!hasAutoJumped.current) {
                hasAutoJumped.current = true;
                const todayStr = isoDate(new Date());
                const ws = getWeekStart(new Date());
                const weekDates = new Set(Array.from({ length: 7 }, (_, i) => isoDate(addDays(ws, i))));
                const dueThisWeek = open.some(t => t.dueDateISO && weekDates.has(t.dueDateISO));
                if (!dueThisWeek) {
                    const next = open
                        .map(t => t.dueDateISO!)
                        .filter(d => d >= todayStr)
                        .sort()[0];
                    if (next) setCurrentDate(new Date(`${next}T12:00:00`));
                }
            }
        });
        // Events and vendor bookings are not fetched: their API routes are
        // disabled (moved to Kalligo), so only the local caches are read.
        setVendorBookings(loadBookings());
        void loadProjectVendors().then(setVendors);
        setAssignments(loadAssignments().filter(a => !a.archived && a.status !== 'cancelled'));
        void refreshAssignmentsFromApi().then(fresh =>
            setAssignments(fresh.filter(a => !a.archived && a.status !== 'cancelled')));
    }, []);

    /** Parse event date string (e.g. "December 9, 2026") to ISO date */
    const parseProjectEventDate = (dateStr: string): string | null => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        return isoDate(d);
    };

    const projectEventsForDay = (day: Date) => {
        const dayStr = isoDate(day);
        return projectEvents.filter(e => parseProjectEventDate(e.date) === dayStr);
    };

    // Vendors booked for a given day, split confirmed vs pending — shown on
    // the event popover so "who's actually locked in for this day" is
    // visible without leaving the calendar. Matches on VendorBooking.eventId
    // against every real event happening that day (a day can have
    // more than one, e.g. ceremony + reception).
    const vendorsForDay = (day: Date) => {
        const eventIds = new Set(projectEventsForDay(day).map(e => e.id));
        if (eventIds.size === 0) return { confirmed: [] as ParsedVendor[], pending: [] as ParsedVendor[] };
        const bookings = Object.values(vendorBookings).filter(b => b.eventId && eventIds.has(b.eventId) && b.status !== 'cancelled' && b.status !== 'rejected');
        const confirmed: ParsedVendor[] = [];
        const pending: ParsedVendor[] = [];
        for (const booking of bookings) {
            const vendor = vendors.find(v => v.apiKey === booking.vendorId);
            if (!vendor) continue;
            (CONFIRMED_STATUSES.has(booking.status) ? confirmed : pending).push(vendor);
        }
        return { confirmed, pending };
    };

    const PROJECT_EVENT_COLORS: Record<string, { bg: string; border: string; text: string }> = {
        'pre-wedding':  { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
        'traditional':  { bg: '#fdf2ff', border: '#e879f9', text: '#9333ea' },
        'ceremony':     { bg: '#eff6ff', border: '#60a5fa', text: '#2563eb' },
        'reception':    { bg: '#f0fdf4', border: '#34d399', text: '#059669' },
        'after-party':  { bg: '#fff7ed', border: '#fb923c', text: '#ea580c' },
    };

    const weekStart = getWeekStart(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    const prevPeriod = () => {
        if (viewMode === 'week') setCurrentDate(d => addDays(d, -7));
        else setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    };

    const nextPeriod = () => {
        if (viewMode === 'week') setCurrentDate(d => addDays(d, 7));
        else setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    };

    const weekEnd = weekDays[weekDays.length - 1];
    const headerLabel = viewMode === 'week'
        ? weekStart.getFullYear() !== weekEnd.getFullYear()
            ? `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()}, ${weekStart.getFullYear()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`
            : weekStart.getMonth() !== weekEnd.getMonth()
                ? `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`
                : `${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getDate()}–${weekEnd.getDate()}, ${weekEnd.getFullYear()}`
        : `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

    useEffect(() => {
        onControlsReady?.({
            viewMode, label: headerLabel, prev: prevPeriod, next: nextPeriod,
            today: () => setCurrentDate(new Date()), setViewMode,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, headerLabel]);

    const isToday = (day: Date) => isoDate(day) === isoDate(new Date());

    const eventsForDay = (day: Date) => {
        const dayStr = isoDate(day);
        return events.filter(e => isoDate(new Date(e.start)) === dayStr);
    };

    const tasksForDay = (day: Date) => {
        const dayStr = isoDate(day);
        return tasks.filter(t => t.dueDateISO === dayStr);
    };

    // Vehicle/driver movements weren't fed into the calendar at all — a
    // pickup_time is exactly the kind of scheduled, timed item this grid is
    // built for. Read-only pills (like the event ones above), not
    // routed through setSelectedEvent/CalendarEvent's edit-delete popover,
    // since an assignment isn't a CalendarEvent and editing it belongs on
    // the Logistics page, not here.
    const assignmentsForDay = (day: Date) => {
        const dayStr = isoDate(day);
        return assignments.filter(a => a.pickup_time && isoDate(new Date(a.pickup_time)) === dayStr);
    };

    const assignmentTop = (a: GuestAssignment) => {
        const d = new Date(a.pickup_time);
        const hours = d.getHours() + d.getMinutes() / 60 - DAY_START_HOUR;
        return Math.max(0, hours * HOUR_HEIGHT);
    };

    const PRIORITY_COLORS: Record<string, string> = {
        P0: '#dc2626', P1: '#ea580c', P2: '#ca8a04', P3: '#6b7280',
    };

    const eventTop = (event: CalendarEvent) => {
        const d = new Date(event.start);
        const hours = d.getHours() + d.getMinutes() / 60 - DAY_START_HOUR;
        return Math.max(0, hours * HOUR_HEIGHT);
    };

    const eventHeight = (event: CalendarEvent) => {
        const start = new Date(event.start);
        const end = new Date(event.end);
        const durationHours = (end.getTime() - start.getTime()) / 3600000;
        return Math.max(durationHours * HOUR_HEIGHT, 28);
    };

    const timeSlots = Array.from({ length: VISIBLE_HOURS }, (_, i) => {
        const h = DAY_START_HOUR + i;
        return h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
    });

    const handleCellClick = (day: Date, hourIndex: number) => {
        const h = DAY_START_HOUR + hourIndex;
        onAddEvent?.({ date: isoDate(day), startTime: `${pad(h)}:00` });
    };

    const handleEditEvent = (event: CalendarEvent) => {
        setSelectedEvent(null);
        onEditEvent?.(event.id);
    };

    const handleDeleteEvent = (id: string) => {
        const updated = events.filter(e => e.id !== id);
        setEvents(updated);
        saveCalendarEvents(updated);
        setSelectedEvent(null);
    };

    // Month view
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startPad = firstDay.getDay();
    const totalCells = Math.ceil((startPad + lastDay.getDate()) / 7) * 7;
    const monthCells: (Date | null)[] = Array.from({ length: totalCells }, (_, i) => {
        const dayNum = i - startPad + 1;
        if (dayNum < 1 || dayNum > lastDay.getDate()) return null;
        return new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNum);
    });

    // Export button (Header): everything on the days this view shows.
    const exportRows: { title: string; date: string; time: string; type: string; detail: string }[] = [];
    (viewMode === 'week' ? weekDays : monthCells.filter((d): d is Date => d !== null)).forEach(day => {
        projectEventsForDay(day).forEach(we => {
            exportRows.push({ title: we.title, date: we.date, time: '', type: we.type, detail: we.location });
        });
        eventsForDay(day).forEach(event => {
            exportRows.push({
                title: event.title,
                date: isoDate(new Date(event.start)),
                time: formatTime(event.start),
                type: event.category,
                detail: event.location || '',
            });
        });
        tasksForDay(day).forEach(task => {
            exportRows.push({ title: task.task, date: task.dueDateISO || '', time: '', type: 'Task', detail: task.status });
        });
    });
    useExport('calendar', { rows: exportRows, ctx: undefined });

    return (
        
            <div className="wt-container fade-in">
              

            {/* Week View */}
            {viewMode === 'week' && (
                <div className="cal-week-wrapper">
                    <div className="cal-week-header">
                        <div className="cal-time-gutter" />
                        {weekDays.map(day => (
                            <div key={day.toISOString()} className={`cal-day-header ${isToday(day) ? 'today' : ''}`}>
                                <span className="cal-day-abbr">{DAY_NAMES[day.getDay()]}</span>
                                <span className={`cal-day-num ${isToday(day) ? 'today' : ''}`}>{day.getDate()}</span>
                            </div>
                        ))}
                    </div>

                    {/* All-day items: events + task deadlines */}
                    {weekDays.some(day => tasksForDay(day).length > 0 || projectEventsForDay(day).length > 0) && (
                        <div className="cal-allday-row">
                            <div className="cal-time-gutter cal-allday-label">All Day</div>
                            {weekDays.map(day => {
                                const dayTasks = tasksForDay(day);
                                const dayProjectEvent = projectEventsForDay(day);
                                return (
                                    <div key={`allday-${day.toISOString()}`} className="cal-allday-cell">
                                        {dayProjectEvent.map(we => {
                                            const colors = PROJECT_EVENT_COLORS[we.type] || PROJECT_EVENT_COLORS['ceremony'];
                                            return (
                                                <div
                                                    key={we.id}
                                                    className="cal-task-pill"
                                                    /* dynamic - exempt from guardrail: event colors computed from category color map */
                                                    // eslint-disable-next-line react/forbid-component-props
                                                    style={{ /* ds-dynamic: event color from palette */ borderLeft: `3px solid ${colors.border}`, background: colors.bg, color: colors.text }}
                                                    title={`${we.title} · ${we.location}`}
                                                >
                                                    <Icon name="calendar" size={10} />
                                                    <span>{we.title}</span>
                                                </div>
                                            );
                                        })}
                                        {dayTasks.map(task => (
                                            <div
                                                key={task.id}
                                                className="cal-task-pill"
                                                /* dynamic - exempt from guardrail: task priority border color from PRIORITY_COLORS map */
                                                // eslint-disable-next-line react/forbid-component-props
                                                style={{ /* ds-dynamic: task priority border color */
                                                    borderLeft: `3px solid ${PRIORITY_COLORS[task.priority] || '#6b7280'}`,
                                                    cursor: 'pointer',
                                                }}
                                                title={`${task.priority} · ${task.assignees?.join(', ') || task.ownerTeam}`}
                                                onClick={e => { e.stopPropagation(); navigate(`/activities?task=${task.id}`); }}
                                            >
                                                <Icon name="check" size={10} />
                                                <span>{task.task}</span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="cal-week-body" ref={scrollRef}>
                        <div className="cal-week-grid">
                            <div className="cal-time-gutter">
                                {timeSlots.map((label, i) => (
                                    <div key={i} className="cal-time-label" style={ { height: HOUR_HEIGHT }}>{label}</div>
                                ))}
                            </div>
                            {weekDays.map(day => (
                                <div
                                    key={day.toISOString()}
                                    className={`cal-day-col ${isToday(day) ? 'today' : ''}`}
                                    style={ { height: VISIBLE_HOURS * HOUR_HEIGHT }}
                                >
                                    {timeSlots.map((_, i) => (
                                        <div
                                            key={i}
                                            className="cal-hour-cell"
                                            style={ { height: HOUR_HEIGHT }}
                                            onClick={() => handleCellClick(day, i)}
                                        />
                                    ))}
                                    {eventsForDay(day).map(event => {
                                        const colors = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.planning;
                                        return (
                                            <div
                                                key={event.id}
                                                className="cal-event"
                                                style={ {
                                                    top: eventTop(event),
                                                    height: eventHeight(event),
                                                    background: colors.bg,
                                                    borderLeft: `3px solid ${colors.border}`,
                                                    color: colors.text,
                                                }}
                                                onClick={e => { e.stopPropagation(); setSelectedEvent(event); }}
                                            >
                                                <div className="cal-event-title">{event.title}</div>
                                                <div className="cal-event-time">
                                                    {formatTime(event.start)}
                                                </div>
                                                {event.location && (
                                                    <div className="cal-event-loc">
                                                        <Icon name="mapPin" size={10} /> {event.location}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {assignmentsForDay(day).map(a => (
                                        <div
                                            key={a.id}
                                            className="cal-event cal-event--logistics"
                                            style={ {
                                                top: assignmentTop(a),
                                                height: 28,
                                                background: '#e0f2fe',
                                                borderLeft: '3px solid #0284c7',
                                                color: '#075985',
                                            }}
                                            title={`${a.movement_type === 'dropoff' ? 'Drop-off' : a.movement_type === 'transfer' ? 'Transfer' : 'Pickup'}: ${a.guest_name}`}
                                            onClick={e => { e.stopPropagation(); navigate('/logistics/schedule'); }}
                                        >
                                            <div className="cal-event-title">{a.guest_name}</div>
                                            <div className="cal-event-time">
                                                {formatTime(a.pickup_time)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Month View */}
            {viewMode === 'month' && (
                <div className="cal-month-wrapper">
                    <div className="cal-month-header">
                        {DAY_NAMES.map(d => <div key={d} className="cal-month-day-name">{d}</div>)}
                    </div>
                    <div className="cal-month-grid">
                        {monthCells.map((day, i) => {
                            if (!day) return <div key={`empty-${i}`} className="cal-month-cell empty" />;
                            const dayEvents = eventsForDay(day);
                            const dayProjectEvents = projectEventsForDay(day);
                            const dayTasks = tasksForDay(day);
                            const today = isToday(day);

                            // One combined, click-handled pill list per day —
                            // events first (most significant), then
                            // tasks, then planning events, so the "top 5"
                            // truncation and its overflow count agree with
                            // what's actually rendered instead of each type
                            // truncating independently against a stale total.
                            type MonthPill = { key: string; label: string; onClick: (e: React.MouseEvent) => void; style: React.CSSProperties };
                            const allPills: MonthPill[] = [
                                ...dayProjectEvents.map((we): MonthPill => {
                                    const colors = PROJECT_EVENT_COLORS[we.type] || PROJECT_EVENT_COLORS['ceremony'];
                                    return {
                                        key: `we-${we.id}`,
                                        label: we.title,
                                        onClick: e => { e.stopPropagation(); },
                                        style: { background: colors.bg, color: colors.text, borderLeft: `2px solid ${colors.border}`, fontWeight: 600 },
                                    };
                                }),
                                ...dayTasks.map((task): MonthPill => ({
                                    key: `task-${task.id}`,
                                    label: task.task,
                                    onClick: e => { e.stopPropagation(); navigate(`/activities?task=${task.id}`); },
                                    style: {
                                        background: CATEGORY_COLORS.task.bg,
                                        color: CATEGORY_COLORS.task.text,
                                        borderLeft: `2px solid ${PRIORITY_COLORS[task.priority] || '#6b7280'}`,
                                    },
                                })),
                                ...dayEvents.map((event): MonthPill => {
                                    const colors = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.planning;
                                    return {
                                        key: `ev-${event.id}`,
                                        label: event.title,
                                        onClick: e => { e.stopPropagation(); setSelectedEvent(event); },
                                        style: { background: colors.bg, color: colors.text, borderLeft: `2px solid ${colors.border}` },
                                    };
                                }),
                            ];
                            const VISIBLE_PILLS = 5;
                            const visiblePills = allPills.slice(0, VISIBLE_PILLS);
                            const hiddenCount = allPills.length - visiblePills.length;

                            return (
                                <div
                                    key={day.toISOString()}
                                    className={`cal-month-cell ${today ? 'today' : ''}`}
                                    onClick={() => { setCurrentDate(day); setViewMode('week'); }}
                                >
                                    <span className={`cal-month-num ${today ? 'today' : ''}`}>{day.getDate()}</span>
                                    <div className="cal-month-pills">
                                        {visiblePills.map(pill => (
                                            /* dynamic - exempt from guardrail: pill colors from category/priority color maps */
                                            // eslint-disable-next-line react/forbid-component-props
                                            <div key={pill.key} className="cal-month-pill" style={pill.style} onClick={pill.onClick}>
                                                {pill.label}
                                            </div>
                                        ))}
                                        {hiddenCount > 0 && (
                                            <div className="cal-month-more">and {hiddenCount} more...</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Event detail popover */}
            {selectedEvent && (
                <div className="cal-popover">
                    <div className="cal-popover-header">
                        <div className="cal-popover-dot" style={ { background: CATEGORY_COLORS[selectedEvent.category]?.border || '#6b7280' }} />
                        <h4>{selectedEvent.title}</h4>
                        <button className="cal-popover-close" onClick={() => setSelectedEvent(null)}>
                            <Icon name="close" size={16} />
                        </button>
                    </div>
                    <div className="cal-popover-body">
                        <div className="cal-popover-row">
                            <Icon name="clock" size={13} />
                            <span>
                                {formatWeekdayDayMonth(selectedEvent.start)}
                                {' · '}
                                {formatTime(selectedEvent.start)}
                                {' – '}
                                {formatTime(selectedEvent.end)}
                            </span>
                        </div>
                        {selectedEvent.location && (
                            <div className="cal-popover-row">
                                <Icon name="mapPin" size={13} />
                                <span>{selectedEvent.location}</span>
                            </div>
                        )}
                        {selectedEvent.description && (
                            <p className="cal-popover-desc">{selectedEvent.description}</p>
                        )}
                        <div
                            className="cal-popover-badge"
                            style={ {
                                background: CATEGORY_COLORS[selectedEvent.category]?.bg,
                                color: CATEGORY_COLORS[selectedEvent.category]?.text,
                            }}
                        >
                            {CATEGORY_OPTIONS.find(c => c.value === selectedEvent.category)?.label || selectedEvent.category}
                        </div>
                        {(() => {
                            const { confirmed, pending } = vendorsForDay(new Date(selectedEvent.start));
                            if (confirmed.length === 0 && pending.length === 0) return null;
                            return (
                                <div className="cal-popover-vendors">
                                    <div className="cal-popover-vendors-row">
                                        <div className="cal-avatar-stack">
                                            {confirmed.slice(0, 4).map(v => (
                                                <div key={v.id} className="cal-avatar cal-avatar--confirmed" title={v.name}>
                                                    {v.name.slice(0, 2).toUpperCase()}
                                                </div>
                                            ))}
                                        </div>
                                        <span className="cal-popover-vendors-label">
                                            {confirmed.length} confirmed
                                        </span>
                                    </div>
                                    {pending.length > 0 && (
                                        <div className="cal-popover-vendors-row">
                                            <div className="cal-avatar-stack">
                                                {pending.slice(0, 4).map(v => (
                                                    <div key={v.id} className="cal-avatar cal-avatar--pending" title={v.name}>
                                                        {v.name.slice(0, 2).toUpperCase()}
                                                    </div>
                                                ))}
                                            </div>
                                            <span className="cal-popover-vendors-label cal-popover-vendors-label--pending">
                                                {pending.length} waiting on confirmation
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                    <div className="cal-popover-footer">
                        <button className="cal-popover-edit" onClick={() => handleEditEvent(selectedEvent)}>
                            Edit
                        </button>
                        <button className="cal-popover-delete" onClick={() => handleDeleteEvent(selectedEvent.id)}>
                            Delete
                        </button>
                    </div>
                </div>
            )}

    </div>

    );
};

export default CalendarView;
